import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { EditorialPageHeader } from "../components/EditorialPageHeader";
import { MarketAvatar } from "../components/MarketAvatar";
import { MarketExternalLinks } from "../components/MarketExternalLinks";
import { MarketCard } from "../components/MarketCard";
import { MarketShareButton } from "../components/MarketShareButton";
import { Seo } from "../components/Seo";
import { MarketCardSkeleton, UpcomingMarketRowSkeleton } from "../components/skeletons";
import { getInitialMarketsSnapshot, initSession, listMarkets } from "../lib/api";
import { applyLiveMarketSnapshotsToWindowGroups } from "../lib/liveMarketSnapshots";
import { classifyMarketsByWindow } from "../lib/marketTime";
import { subscribeRealtime } from "../lib/realtime";
import { formatMarketTotalVotes } from "../lib/marketTokenDisplay.js";
import { formatCurrentUtcLine, formatUtcDateTime, useCurrentUtcTime } from "../lib/timeDisplay";
import { useWalletAuth } from "../lib/walletAuth";
import { buildSiteSeo } from "../../shared/seo";

export function MarketsPage() {
  const [initialMarketsSnapshot] = useState(() => getInitialMarketsSnapshot());
  const hasInitialMarkets = initialMarketsSnapshot.length > 0;
  const initialMarkets = useMemo(() => classifyMarketsByWindow(initialMarketsSnapshot), [initialMarketsSnapshot]);
  const [activeMarkets, setActiveMarkets] = useState(() => initialMarkets.active);
  const [upcomingMarkets, setUpcomingMarkets] = useState(() => initialMarkets.upcoming);
  const [closedMarkets, setClosedMarkets] = useState(() => initialMarkets.closed);
  const marketGroupsRef = useRef(initialMarkets);
  const [marketsLoaded, setMarketsLoaded] = useState(() => hasInitialMarkets);
  const [marketStatus, setMarketStatus] = useState(() => (hasInitialMarkets ? "success" : "pending"));
  const [marketsEverSucceeded, setMarketsEverSucceeded] = useState(() => hasInitialMarkets);
  const { session } = useWalletAuth();
  const currentUtc = useCurrentUtcTime();
  const affiliateId = session?.walletAddress || session?.userId || "";
  const hasMarketRows = activeMarkets.length > 0 || upcomingMarkets.length > 0 || closedMarkets.length > 0;
  const showBoardError = marketsLoaded && !marketsEverSucceeded && !hasMarketRows;
  const showFirstLoadSkeleton = !marketsLoaded;
  const showActiveSkeleton = showFirstLoadSkeleton;
  const showUpcomingSkeleton = showFirstLoadSkeleton;
  const showClosedSkeleton = showFirstLoadSkeleton;

  const refresh = useCallback(async () => {
    setMarketStatus((currentStatus) => (currentStatus === "success" ? currentStatus : "pending"));
    try {
      const data = await listMarkets();
      const grouped = classifyMarketsByWindow(data);
      marketGroupsRef.current = grouped;
      setActiveMarkets(grouped.active);
      setUpcomingMarkets(grouped.upcoming);
      setClosedMarkets(grouped.closed);
      setMarketStatus("success");
      setMarketsEverSucceeded(true);
    } catch (error) {
      setMarketStatus("error");
      throw error;
    } finally {
      setMarketsLoaded(true);
    }
  }, []);

  const handleLiveMarketUpdate = useCallback(
    (payload) => {
      const grouped = applyLiveMarketSnapshotsToWindowGroups({
        ...marketGroupsRef.current,
        snapshots: payload
      });
      if (!grouped.changed) {
        refresh().catch(() => null);
        return;
      }
      marketGroupsRef.current = grouped;
      setActiveMarkets(grouped.active);
      setUpcomingMarkets(grouped.upcoming);
      setClosedMarkets(grouped.closed);
      setMarketStatus("success");
      setMarketsEverSucceeded(true);
      setMarketsLoaded(true);
    },
    [refresh]
  );

  useEffect(() => {
    initSession().catch(() => null);
    refresh().catch(() => null);
  }, [refresh]);

  useEffect(() => {
    return subscribeRealtime({
      topics: ["home"],
      onMarketUpdate: handleLiveMarketUpdate
    });
  }, [handleLiveMarketUpdate]);

  return (
    <>
      <Seo metadata={buildSiteSeo({ path: "/markets" })} />
      <section className="space-y-4" data-route="markets-list">
        <EditorialPageHeader
          eyebrow="Live board"
          title="Find the markets moving right now."
          description="See what is live, what is next, and where your vote can still swing the board."
          meta={<span>{formatCurrentUtcLine(currentUtc)}</span>}
          actions={<MarketShareButton affiliateId={affiliateId} variant="homepage" className="shrink-0 self-start" />}
        />

        {showBoardError ? (
          <div className="route-shell space-y-3">
            <h2 className="text-lg font-semibold text-zinc-100">Markets are unavailable right now.</h2>
            <p className="max-w-2xl text-sm leading-6 text-zinc-400">
              We could not load the market board. Try again in a moment.
            </p>
            <button type="button" className="theme-button-primary" onClick={() => refresh().catch(() => null)}>
              Retry
            </button>
          </div>
        ) : (
          <>
            <article className="route-shell space-y-3">
              <h2 className="text-lg font-semibold text-blue-400">Live now</h2>
              {showActiveSkeleton ? (
                <div className="grid gap-4 md:grid-cols-2">
                  <MarketCardSkeleton />
                  <MarketCardSkeleton />
                </div>
              ) : marketStatus === "error" && activeMarkets.length === 0 && hasMarketRows ? (
                <div className="space-y-2">
                  <p className="text-sm leading-6 text-zinc-400">
                    We could not load the active board. Try again in a moment.
                  </p>
                  <button type="button" className="theme-button-primary" onClick={() => refresh().catch(() => null)}>
                    Retry
                  </button>
                </div>
              ) : (marketStatus === "success" || marketsEverSucceeded) && activeMarkets.length === 0 ? (
                <div className="space-y-2">
                  <p className="text-sm leading-6 text-zinc-400">
                    No markets are live right now. Queued markets move here automatically when their voting windows open.
                  </p>
                  <Link to="/create" className="theme-button-secondary">
                    Create the next market
                  </Link>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {activeMarkets.map((market) => (
                    <MarketCard key={market.id} market={market} />
                  ))}
                </div>
              )}
            </article>

            <article className="route-shell space-y-3">
              <h2 className="text-lg font-semibold text-blue-400">Upcoming</h2>
              {showUpcomingSkeleton ? (
                <div className="grid gap-3">
                  <UpcomingMarketRowSkeleton />
                  <UpcomingMarketRowSkeleton />
                </div>
              ) : marketStatus === "error" && upcomingMarkets.length === 0 && hasMarketRows ? (
                <div className="space-y-2">
                  <p className="text-sm leading-6 text-zinc-400">
                    We could not load upcoming markets. Try again in a moment.
                  </p>
                  <button type="button" className="theme-button-primary" onClick={() => refresh().catch(() => null)}>
                    Retry
                  </button>
                </div>
              ) : (marketStatus === "success" || marketsEverSucceeded) && upcomingMarkets.length === 0 ? (
                <p className="text-sm leading-6 text-zinc-400">
                  Nothing is queued yet. Launch the next market and give the crowd something to move.
                </p>
              ) : (
                <div className="grid gap-3">
                  {upcomingMarkets.map((market) => (
                    <MarketListRow key={market.id} market={market} affiliateId={affiliateId} />
                  ))}
                </div>
              )}
            </article>

            <article className="route-shell space-y-3">
              <h2 className="text-lg font-semibold text-blue-400">Closed markets</h2>
              {showClosedSkeleton ? (
                <div className="grid gap-3">
                  <UpcomingMarketRowSkeleton label="Loading closed market" />
                  <UpcomingMarketRowSkeleton label="Loading closed market" />
                </div>
              ) : marketStatus === "error" && closedMarkets.length === 0 && hasMarketRows ? (
                <div className="space-y-2">
                  <p className="text-sm leading-6 text-zinc-400">
                    We could not load closed markets. Try again in a moment.
                  </p>
                  <button type="button" className="theme-button-primary" onClick={() => refresh().catch(() => null)}>
                    Retry
                  </button>
                </div>
              ) : (marketStatus === "success" || marketsEverSucceeded) && closedMarkets.length === 0 ? (
                <p className="text-sm leading-6 text-zinc-400">
                  No markets have closed yet. Finished boards will appear here after voting windows end.
                </p>
              ) : (
                <div className="grid gap-3">
                  {closedMarkets.map((market) => (
                    <MarketListRow key={market.id} market={market} affiliateId={affiliateId} variant="closed" />
                  ))}
                </div>
              )}
            </article>
          </>
        )}
      </section>
    </>
  );
}

function MarketListRow({ market, affiliateId, variant = "upcoming" }) {
  const closed = variant === "closed";

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-3" data-cls-measure="upcoming-market-row">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex min-w-0 items-start gap-2">
          <MarketAvatar imageUrl={market.imageUrl} title={market.title} size="sm" />
          <h4 className="min-w-0 font-semibold text-zinc-100">
            <Link to={`/market/${market.id}`} className="hover:text-blue-300">
              {market.title}
            </Link>
          </h4>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:justify-end">
          <span className="text-xs text-zinc-400">{formatMarketTotalVotes(market)}</span>
          <MarketShareButton market={market} affiliateId={affiliateId} />
        </div>
      </div>
      <p className="mt-1 text-sm text-zinc-300">{market.summary}</p>
      <MarketExternalLinks xUrl={market.xUrl} websiteUrl={market.websiteUrl} className="mt-2" />
      <p className="mt-2 text-xs text-zinc-400">
        {closed
          ? `Ended (UTC) ${formatUtcDateTime(market.endAt)}`
          : `Starts (UTC) ${formatUtcDateTime(market.startAt)} | Ends (UTC) ${formatUtcDateTime(market.endAt)}`}
      </p>
    </div>
  );
}
